/**
 * AI Assistant History
 *
 * Collapsible list of previous conversations grouped by time period.
 */

import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
  Skeleton,
  alpha,
} from '@mui/material';
import { AddIcon, CloseIcon, DeleteOutlineIcon, DownloadIcon, ExpandLessIcon, ExpandMoreIcon, PrintIcon, RateReviewIcon } from "../../icons";
import { useRootStore } from '../../store/contexts';
import type { ConversationSummary } from './AiAssistantStore';

interface HistoryItemProps {
  conversation: ConversationSummary;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  formatTime: (date: Date) => string;
}

const HistoryItem: React.FC<HistoryItemProps> = ({
  conversation,
  isSelected,
  onSelect,
  onDelete,
  formatTime,
}) => {
  const [showDelete, setShowDelete] = useState(false);

  return (
    <ListItemButton
      selected={isSelected}
      onClick={onSelect}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
      sx={{
        borderRadius: 1,
        py: 0.75,
        px: 1.5,
        mb: 0.5,
        '&.Mui-selected': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        },
      }}
    >
      <ListItemText
        primary={conversation.preview}
        secondary={formatTime(conversation.lastMessageAt)}
        primaryTypographyProps={{
          variant: 'body2',
          noWrap: true,
          sx: { fontWeight: isSelected ? 500 : 400 },
        }}
        secondaryTypographyProps={{
          variant: 'caption',
          color: 'text.secondary',
        }}
      />
      {showDelete && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            ml: 1,
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </ListItemButton>
  );
};

interface AiAssistantHistoryProps {
  onPrint?: () => void;
}

export const AiAssistantHistory: React.FC<AiAssistantHistoryProps> = observer(({ onPrint }) => {
  const { aiAssistantStore } = useRootStore();
  const [isExpanded, setIsExpanded] = useState(true);

  const handleSelectConversation = (id: string) => {
    aiAssistantStore.loadConversation(id);
  };

  const handleDeleteConversation = async (id: string) => {
    if (window.confirm('¿Eliminar esta conversación?')) {
      await aiAssistantStore.deleteConversation(id);
    }
  };

  if (aiAssistantStore.isLoadingHistory) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 0.5 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 0.5 }} />
        <Skeleton variant="rectangular" height={40} />
      </Box>
    );
  }

  const groups = aiAssistantStore.groupedConversations;

  const handleExport = (e: React.MouseEvent) => {
    e.stopPropagation();
    aiAssistantStore.exportToMarkdown();
  };

  const handleNewConversation = (e: React.MouseEvent) => {
    e.stopPropagation();
    aiAssistantStore.newConversation();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    aiAssistantStore.close();
  };

  const handleToggleEvaluation = (e: React.MouseEvent) => {
    e.stopPropagation();
    aiAssistantStore.toggleEvaluation();
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          px: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ mr: 0.5 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Historial
        </Typography>
        {aiAssistantStore.messages.length > 0 && (
          <>
            <IconButton size="small" onClick={handleExport} title="Exportar a Markdown">
              <DownloadIcon fontSize="small" />
            </IconButton>
            {onPrint && (
              <IconButton size="small" onClick={onPrint} title="Imprimir">
                <PrintIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
        <IconButton
          size="small"
          onClick={handleToggleEvaluation}
          title={aiAssistantStore.evaluationEnabled ? "Deshabilitar evaluaciones" : "Habilitar evaluaciones"}
          sx={{
            color: aiAssistantStore.evaluationEnabled ? 'primary.main' : 'inherit',
          }}
        >
          <RateReviewIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleNewConversation} title="Nueva conversación">
          <AddIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleClose} title="Cerrar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ maxHeight: 200, overflow: 'auto', px: 1, py: 1 }}>
          {groups.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 1.5, display: 'block', py: 2, textAlign: 'center' }}
            >
              No hay conversaciones previas
            </Typography>
          ) : (
            groups.map((group) => (
              <Box key={group.label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 1.5, display: 'block', mt: 1, mb: 0.5 }}
                >
                  {group.label}
                </Typography>
                <List dense disablePadding>
                  {group.conversations.map((conv) => (
                    <HistoryItem
                      key={conv.id}
                      conversation={conv}
                      isSelected={aiAssistantStore.currentConversationId === conv.id}
                      onSelect={() => handleSelectConversation(conv.id)}
                      onDelete={() => handleDeleteConversation(conv.id)}
                      formatTime={(date) => aiAssistantStore.formatRelativeTime(date)}
                    />
                  ))}
                </List>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

export default AiAssistantHistory;
